/**
 * FriendsBubble — Fixed bottom-right social widget
 * 
 * Features:
 *   - Collapsible bubble showing online friends count
 *   - Expanded panel with friends list (online/offline sections)
 *   - Add friends by username search
 *   - Accept/reject incoming friend requests
 *   - Whisper (private messaging) with inline chat
 *   - Spectate friend's game (link to their session)
 *   - Presence tracking (online/away/offline)
 * 
 * Uses Supabase Realtime for live presence + whisper delivery.
 * Falls back to polling when realtime unavailable.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ManaSymbol } from './ManaSymbol';
import {
    Users, MessageCircle, Search, Plus, X, ChevronDown, ChevronUp,
    Send, Check, XCircle, Eye, Loader2, Circle, UserPlus, UserMinus,
    Bell, Settings, Minus, Maximize2, Minimize2, Copy, Hash, Crosshair, Clock, Trash2
} from 'lucide-react';
import { supabase, authService, sparksService } from '../lib/supabase';

// ===== Types =====

interface Friend {
    id: string;            // friendship id
    oderId: string;     // the other user's id
    displayName: string;
    avatarUrl?: string;
    status: 'online' | 'away' | 'busy' | 'offline';
    currentActivity?: string;
    lastSeenAt?: string;
    friendshipStatus: 'accepted' | 'pending';
    isRequester: boolean;  // did they send the request?
}

interface Whisper {
    id: string;
    senderId: string;
    recipientId: string;
    content: string;
    isRead: boolean;
    createdAt: string;
}

interface WhisperThread {
    oderId: string;
    displayName: string;
    avatarUrl?: string;
    lastMessage?: string;
    lastMessageAt?: string;
    unreadCount: number;
    messages: Whisper[];
}

type BubbleView = 'friends' | 'chat' | 'requests' | 'add' | 'lfg';

interface LfgGame {
    id: string;
    hostId: string;
    hostDisplayName: string;
    format: string;
    platform: 'arena' | 'mtgo' | 'session';
    maxPlayers: number;
    currentPlayers: number;
    message: string;
    queueOpen: boolean;
    status: 'open' | 'full' | 'started' | 'closed';
    createdAt: string;
    expiresAt: string;
}

interface LfgPlayer {
    id: string;
    gameId: string;
    userId: string;
    displayName: string;
    status: 'pending' | 'accepted' | 'declined' | 'kicked';
    joinedAt: string;
    respondedAt?: string;
}

// ===== DECKSTR AI Bot =====
const DECKSTR_BOT_USER_ID = '00000000-0000-0000-0000-dec570000000';

const DECKSTR_BOT_FRIEND: Friend = {
    id: 'deckstr-bot',
    oderId: DECKSTR_BOT_USER_ID,
    displayName: 'DECKSTR.GG',
    avatarUrl: '/jud.mp4',
    status: 'online',
    currentActivity: 'Ask me anything about MTG!',
    friendshipStatus: 'accepted',
    isRequester: false,
};

const BOT_WELCOME_MESSAGE = '🤖 Hey! I\'m **DECKSTR.GG**, your MTG assistant.\n\n' +
    'Try commands like:\n' +
    '• `!card lightning bolt` — Card lookup\n' +
    '• `!price counterspell` — Market prices\n' +
    '• `!prob 60 4 7` — Draw probability\n' +
    '• `!explain commander` — Rules topics\n' +
    '• `!help` — Full command list\n\n' +
    'Or just ask me anything about Magic! 🃏';

// ─── Render bot message with mana symbols + markdown ─────────
// Parses {W}, {U}, {B}, {R}, {G}, {C}, {0}-{20}, {X}, {T}, {S}, etc.
// into Scryfall SVG images, and handles **bold** + `code` markdown.
const MANA_SYMBOL_RE = /\{([WUBRGCXSTPQE]|\d{1,2}(?:\/[WUBRG])?|[WUBRG]\/[WUBRG]|[WUBRG]\/P|H[WUBRG]|∞)\}/g;

function renderBotContent(text: string): React.ReactNode {
    // First split on mana symbols, bold, and code
    // Process in order: mana symbols → bold → code
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;

    // Combined regex: mana symbols, bold, inline code
    const COMBINED_RE = /(\{(?:[WUBRGCXSTPQE]|\d{1,2}(?:\/[WUBRG])?|[WUBRG]\/[WUBRG]|[WUBRG]\/P|H[WUBRG]|∞)\}|\*\*(.+?)\*\*|`([^`]+)`)/g;

    let lastIndex = 0;
    let match;

    while ((match = COMBINED_RE.exec(remaining)) !== null) {
        if (match.index > lastIndex) {
            parts.push(remaining.slice(lastIndex, match.index));
        }

        const full = match[0];
        if (full.startsWith('{') && full.endsWith('}')) {
            // Mana symbol
            const sym = full.slice(1, -1);
            parts.push(
                <ManaSymbol
                    key={key++}
                    symbol={sym}
                    size="sm"
                    className="inline-block align-middle mx-0.5"
                />
            );
        } else if (match[2] !== undefined) {
            // **bold**
            parts.push(<strong key={key++} className="font-semibold">{match[2]}</strong>);
        } else if (match[3] !== undefined) {
            // `code`
            parts.push(
                <code key={key++} className="bg-white/10 text-cyan-300 px-1 py-0.5 rounded text-[10px] font-mono">
                    {match[3]}
                </code>
            );
        }

        lastIndex = match.index + full.length;
    }

    // Push remaining text
    if (lastIndex < remaining.length) {
        parts.push(remaining.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
}

const PRESENCE_INTERVAL_MS = 30_000; // heartbeat every 30s
const STALE_THRESHOLD_MS = 90_000;   // offline after 90s no heartbeat

export function FriendsBubble() {
    // Core state
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [activeView, setActiveView] = useState<BubbleView>('friends');

    // Friends data
    const [friends, setFriends] = useState<Friend[]>([]);
    const [pendingRequests, setPendingRequests] = useState<Friend[]>([]);
    const [isLoadingFriends, setIsLoadingFriends] = useState(false);

    // Add friend
    const [searchUsername, setSearchUsername] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [addStatus, setAddStatus] = useState<string | null>(null);

    // Whispers
    const [threads, setThreads] = useState<WhisperThread[]>([]);
    const [activeChatUserId, setActiveChatUserId] = useState<string | null>(null);
    const [chatMessages, setChatMessages] = useState<Whisper[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [unreadTotal, setUnreadTotal] = useState(0);

    // Friend tag (Discord-style #1234)
    const [myTag, setMyTag] = useState<string | null>(null);
    const [tagCopied, setTagCopied] = useState(false);

    // LFG — Host-controlled game matchmaking
    const [lfgGames, setLfgGames] = useState<LfgGame[]>([]);
    const [lfgPlayers, setLfgPlayers] = useState<Record<string, LfgPlayer[]>>({}); // gameId → players
    const [lfgFormat, setLfgFormat] = useState('Standard');
    const [lfgPlatform, setLfgPlatform] = useState<'arena' | 'mtgo' | 'session'>('session');
    const [lfgMaxPlayers, setLfgMaxPlayers] = useState(4);
    const [lfgMessage, setLfgMessage] = useState('');
    const [isPostingLfg, setIsPostingLfg] = useState(false);
    const [isLoadingLfg, setIsLoadingLfg] = useState(false);
    const [expandedGame, setExpandedGame] = useState<string | null>(null);

    const chatEndRef = useRef<HTMLDivElement>(null);
    const messageInputRef = useRef<HTMLInputElement>(null);

    // ===== Auth listener =====
    useEffect(() => {
        const checkAuth = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            setCurrentUser(user || null);
        };
        checkAuth();

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setCurrentUser(session?.user || null);
        });

        return () => subscription.unsubscribe();
    }, []);

    // ===== Load friends when user is available =====
    const loadFriends = useCallback(async () => {
        if (!currentUser) return;
        setIsLoadingFriends(true);

        try {
            // Get all friendships for this user
            const { data: friendships, error } = await supabase
                .from('friendships')
                .select('*')
                .or(`requester_id.eq.${currentUser.id},addressee_id.eq.${currentUser.id}`);

            if (error || !friendships) {
                setIsLoadingFriends(false);
                return;
            }

            // Get other user IDs
            const otherIds = friendships.map(f =>
                f.requester_id === currentUser.id ? f.addressee_id : f.requester_id
            );

            if (otherIds.length === 0) {
                setFriends([]);
                setPendingRequests([]);
                setIsLoadingFriends(false);
                return;
            }

            // Get presence info for all friends
            const { data: presenceData } = await supabase
                .from('user_presence')
                .select('*')
                .in('user_id', otherIds);

            const presenceMap = new Map(
                (presenceData || []).map(p => [p.user_id, p])
            );

            // Build friend list
            const allFriends: Friend[] = friendships.map(f => {
                const oderId = f.requester_id === currentUser.id ? f.addressee_id : f.requester_id;
                const presence = presenceMap.get(oderId);
                const lastSeen = presence?.last_seen_at ? new Date(presence.last_seen_at).getTime() : 0;
                const isStale = Date.now() - lastSeen > STALE_THRESHOLD_MS;

                return {
                    id: f.id,
                    oderId,
                    displayName: presence?.display_name || 'Unknown',
                    avatarUrl: presence?.avatar_url,
                    status: isStale ? 'offline' : (presence?.status || 'offline'),
                    currentActivity: presence?.current_activity,
                    lastSeenAt: presence?.last_seen_at,
                    friendshipStatus: f.status,
                    isRequester: f.requester_id === currentUser.id,
                };
            });

            setFriends(allFriends.filter(f => f.friendshipStatus === 'accepted'));
            setPendingRequests(allFriends.filter(f =>
                f.friendshipStatus === 'pending' && !f.isRequester
            ));
        } catch (err) {
            console.error('Failed to load friends:', err);
        }

        setIsLoadingFriends(false);
    }, [currentUser]);

    useEffect(() => {
        if (currentUser) {
            loadFriends();
        } else {
            setFriends([]);
            setPendingRequests([]);
        }
    }, [currentUser, loadFriends]);

    // ===== Presence heartbeat + friend tag assignment =====
    useEffect(() => {
        if (!currentUser) return;

        const heartbeat = async () => {
            const displayName = currentUser.user_metadata?.display_name ||
                currentUser.email?.split('@')[0] || 'Planeswalker';

            await supabase
                .from('user_presence')
                .upsert({
                    user_id: currentUser.id,
                    display_name: displayName,
                    avatar_url: currentUser.user_metadata?.avatar_url || null,
                    status: 'online',
                    last_seen_at: new Date().toISOString(),
                }, { onConflict: 'user_id' });
        };

        // Ensure the user has a friend tag (Discord-style #1234)
        const ensureTag = async () => {
            // Check local metadata first
            const existingTag = currentUser.user_metadata?.friend_tag;
            if (existingTag) {
                setMyTag(existingTag);
                return;
            }
            // Call RPC to assign one
            try {
                const { data, error } = await supabase
                    .rpc('ensure_friend_tag', { target_user_id: currentUser.id });
                if (!error && data) {
                    setMyTag(data);
                    // Refresh local session so metadata includes the tag
                    await supabase.auth.refreshSession();
                }
            } catch { /* ignore — tag will be assigned next session */ }
        };

        heartbeat();
        ensureTag();
        const interval = setInterval(heartbeat, PRESENCE_INTERVAL_MS);

        // Set offline on unload
        const handleUnload = () => {
            // Fire-and-forget update; we can't await here
            supabase
                .from('user_presence')
                .update({ status: 'offline', last_seen_at: new Date().toISOString() })
                .eq('user_id', currentUser.id)
                .then(() => { });
        };
        window.addEventListener('beforeunload', handleUnload);

        return () => {
            clearInterval(interval);
            window.removeEventListener('beforeunload', handleUnload);
        };
    }, [currentUser]);

    // ===== Periodically refresh friends (presence changes) =====
    useEffect(() => {
        if (!currentUser || !isOpen) return;
        const interval = setInterval(loadFriends, PRESENCE_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [currentUser, isOpen, loadFriends]);

    // ===== Search users to add =====
    const searchUsers = async (query: string) => {
        if (query.length < 2 || !currentUser) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);

        try {
            // Use RPC that searches auth.users directly — finds ALL registered
            // users, not just those with a user_presence heartbeat row.
            const { data, error } = await supabase
                .rpc('search_users_for_friend', {
                    search_query: query,
                    current_user_id: currentUser.id,
                });

            if (!error && data) {
                // Filter out existing friends / pending requests
                const friendIds = new Set([
                    ...friends.map(f => f.oderId),
                    ...pendingRequests.map(f => f.oderId),
                ]);
                setSearchResults(data.filter((u: any) => !friendIds.has(u.user_id)));
            } else {
                // Fallback: search user_presence if RPC unavailable
                const { data: fallbackData } = await supabase
                    .from('user_presence')
                    .select('user_id, display_name, avatar_url')
                    .ilike('display_name', `%${query}%`)
                    .neq('user_id', currentUser.id)
                    .limit(10);

                if (fallbackData) {
                    const friendIds = new Set([
                        ...friends.map(f => f.oderId),
                        ...pendingRequests.map(f => f.oderId),
                    ]);
                    setSearchResults(fallbackData.filter(u => !friendIds.has(u.user_id)));
                }
            }
        } catch { /* ignore */ }

        setIsSearching(false);
    };

    // ===== Send friend request =====
    const sendFriendRequest = async (addresseeId: string) => {
        if (!currentUser) return;

        try {
            const { error } = await supabase
                .from('friendships')
                .insert({
                    requester_id: currentUser.id,
                    addressee_id: addresseeId,
                    status: 'pending',
                });

            if (error) {
                if (error.code === '23505') {
                    setAddStatus('Already friends or request pending');
                } else {
                    setAddStatus('Failed to send request');
                }
            } else {
                setAddStatus('Friend request sent!');
                setSearchResults(prev => prev.filter(u => u.user_id !== addresseeId));
            }

            setTimeout(() => setAddStatus(null), 3000);
        } catch { /* ignore */ }
    };

    // ===== Accept/reject friend request =====
    const handleRequest = async (friendshipId: string, accept: boolean) => {
        try {
            if (accept) {
                await supabase
                    .from('friendships')
                    .update({ status: 'accepted' })
                    .eq('id', friendshipId);

                // Check friendship milestones for spark rewards (async, non-blocking)
                sparksService.checkFriendshipMilestones().then(result => {
                    if (result.sparksAwarded > 0) {
                        console.log(`⚡ Friendship milestone! +${result.sparksAwarded} sparks (${result.friendCount} friends)`);
                    }
                });
            } else {
                await supabase
                    .from('friendships')
                    .delete()
                    .eq('id', friendshipId);
            }
            await loadFriends();
        } catch { /* ignore */ }
    };

    // ===== Remove friend =====
    const removeFriend = async (friendshipId: string) => {
        try {
            await supabase
                .from('friendships')
                .delete()
                .eq('id', friendshipId);
            await loadFriends();
        } catch { /* ignore */ }
    };

    // ===== Load whisper threads =====
    const loadWhisperThreads = useCallback(async () => {
        if (!currentUser) return;

        try {
            const { data: whispers } = await supabase
                .from('whispers')
                .select('*')
                .or(`sender_id.eq.${currentUser.id},recipient_id.eq.${currentUser.id}`)
                .order('created_at', { ascending: false })
                .limit(200);

            if (!whispers) return;

            // Group by conversation partner
            const threadMap = new Map<string, WhisperThread>();

            for (const w of whispers) {
                const oderId = w.sender_id === currentUser.id ? w.recipient_id : w.sender_id;
                if (!threadMap.has(oderId)) {
                    const friend = friends.find(f => f.oderId === oderId);
                    threadMap.set(oderId, {
                        oderId,
                        displayName: friend?.displayName || 'Unknown',
                        avatarUrl: friend?.avatarUrl,
                        unreadCount: 0,
                        messages: [],
                    });
                }
                const thread = threadMap.get(oderId)!;
                thread.messages.push({
                    id: w.id,
                    senderId: w.sender_id,
                    recipientId: w.recipient_id,
                    content: w.content,
                    isRead: w.is_read,
                    createdAt: w.created_at,
                });
                if (!thread.lastMessageAt || w.created_at > thread.lastMessageAt) {
                    thread.lastMessage = w.content;
                    thread.lastMessageAt = w.created_at;
                }
                if (w.recipient_id === currentUser.id && !w.is_read) {
                    thread.unreadCount++;
                }
            }

            // Reverse messages in each thread (so oldest first)
            for (const thread of threadMap.values()) {
                thread.messages.reverse();
            }

            const sorted = Array.from(threadMap.values())
                .sort((a, b) => new Date(b.lastMessageAt || 0).getTime() - new Date(a.lastMessageAt || 0).getTime());

            setThreads(sorted);
            setUnreadTotal(sorted.reduce((sum, t) => sum + t.unreadCount, 0));
        } catch { /* ignore */ }
    }, [currentUser, friends]);

    useEffect(() => {
        if (currentUser && friends.length >= 0) {
            loadWhisperThreads();
        }
    }, [currentUser, friends, loadWhisperThreads]);

    // Sync chatMessages when threads update (so new messages appear automatically)
    // Also auto-mark as read if chat is currently open
    useEffect(() => {
        if (activeChatUserId) {
            const thread = threads.find(t => t.oderId === activeChatUserId);
            if (thread && thread.messages.length > 0) {
                setChatMessages(thread.messages);
                setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

                // Auto-mark incoming messages as read while chat is open
                if (currentUser && thread.unreadCount > 0) {
                    supabase
                        .from('whispers')
                        .update({ is_read: true })
                        .eq('recipient_id', currentUser.id)
                        .eq('sender_id', activeChatUserId)
                        .eq('is_read', false)
                        .then(() => {
                            // Update local state immediately without full reload
                            setThreads(prev => prev.map(t =>
                                t.oderId === activeChatUserId
                                    ? { ...t, unreadCount: 0, messages: t.messages.map(m => ({ ...m, isRead: true })) }
                                    : t
                            ));
                            setUnreadTotal(prev => Math.max(0, prev - thread.unreadCount));
                        });
                }
            }
        }
    }, [threads, activeChatUserId, currentUser]);

    // Poll for bot replies every 3s when chatting with bot
    useEffect(() => {
        if (activeChatUserId !== DECKSTR_BOT_USER_ID) return;
        const iv = setInterval(() => loadWhisperThreads(), 3000);
        return () => clearInterval(iv);
    }, [activeChatUserId, loadWhisperThreads]);

    // Auto-load LFG games when tab is opened
    const loadLfgGames = async () => {
        setIsLoadingLfg(true);
        try {
            const { data } = await supabase
                .from('lfg_games')
                .select('*')
                .in('status', ['open', 'full'])
                .gte('expires_at', new Date().toISOString())
                .order('created_at', { ascending: false })
                .limit(30);
            if (data) {
                const games: LfgGame[] = data.map((g: any) => ({
                    id: g.id, hostId: g.host_id, hostDisplayName: g.host_display_name,
                    format: g.format, platform: g.platform, maxPlayers: g.max_players,
                    currentPlayers: g.current_players, message: g.message,
                    queueOpen: g.queue_open, status: g.status,
                    createdAt: g.created_at, expiresAt: g.expires_at,
                }));
                setLfgGames(games);
                // Load players for games where current user is host or participant
                const gameIds = games.map(g => g.id);
                if (gameIds.length > 0) {
                    const { data: playersData } = await supabase
                        .from('lfg_players')
                        .select('*')
                        .in('game_id', gameIds);
                    if (playersData) {
                        const grouped: Record<string, LfgPlayer[]> = {};
                        for (const p of playersData) {
                            const gid = p.game_id;
                            if (!grouped[gid]) grouped[gid] = [];
                            grouped[gid].push({
                                id: p.id, gameId: p.game_id, userId: p.user_id,
                                displayName: p.display_name, status: p.status,
                                joinedAt: p.joined_at, respondedAt: p.responded_at,
                            });
                        }
                        setLfgPlayers(grouped);
                    }
                }
            }
        } catch { /* ignore */ }
        setIsLoadingLfg(false);
    };

    useEffect(() => {
        if (activeView !== 'lfg' || !currentUser) return;
        loadLfgGames();

        // Realtime: auto-refresh when games or players change
        const gamesChannel = supabase
            .channel('lfg-games-realtime')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'lfg_games' }, () => {
                loadLfgGames();
            })
            .subscribe();

        const playersChannel = supabase
            .channel('lfg-players-realtime')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'lfg_players' }, () => {
                loadLfgGames();
            })
            .subscribe();

        return () => {
            supabase.removeChannel(gamesChannel);
            supabase.removeChannel(playersChannel);
        };
    }, [activeView, currentUser]);

    // LFG host actions
    const handleCreateGame = async () => {
        if (!currentUser || isPostingLfg) return;
        setIsPostingLfg(true);
        try {
            const name = currentUser.user_metadata?.display_name || currentUser.email?.split('@')[0] || 'Anon';
            await supabase.from('lfg_games').insert({
                host_id: currentUser.id,
                host_display_name: name,
                format: lfgFormat,
                platform: lfgPlatform,
                max_players: lfgMaxPlayers,
                message: lfgMessage || `Looking for ${lfgFormat} game`,
            });
            setLfgMessage('');
            await loadLfgGames();
        } catch { /* ignore */ }
        setIsPostingLfg(false);
    };

    const handleJoinGame = async (gameId: string) => {
        if (!currentUser) return;
        const name = currentUser.user_metadata?.display_name || currentUser.email?.split('@')[0] || 'Anon';
        try {
            await supabase.from('lfg_players').insert({
                game_id: gameId,
                user_id: currentUser.id,
                display_name: name,
            });
            await loadLfgGames();
        } catch { /* ignore */ }
    };

    const handleRespondToPlayer = async (playerId: string, newStatus: 'accepted' | 'declined') => {
        try {
            await supabase.from('lfg_players').update({
                status: newStatus,
                responded_at: new Date().toISOString(),
            }).eq('id', playerId);
            await loadLfgGames();
        } catch { /* ignore */ }
    };

    const handleToggleQueue = async (gameId: string, newState: boolean) => {
        try {
            await supabase.from('lfg_games').update({ queue_open: newState }).eq('id', gameId);
            setLfgGames(prev => prev.map(g => g.id === gameId ? { ...g, queueOpen: newState } : g));
        } catch { /* ignore */ }
    };

    const handleCloseGame = async (gameId: string) => {
        try {
            await supabase.from('lfg_games').update({ status: 'closed' }).eq('id', gameId);
            setLfgGames(prev => prev.filter(g => g.id !== gameId));
        } catch { /* ignore */ }
    };

    const handleLeaveGame = async (gameId: string) => {
        if (!currentUser) return;
        try {
            await supabase.from('lfg_players').delete()
                .eq('game_id', gameId)
                .eq('user_id', currentUser.id);
            await loadLfgGames();
        } catch { /* ignore */ }
    };

    const getPlayerStatus = (gameId: string): LfgPlayer | undefined => {
        return lfgPlayers[gameId]?.find(p => p.userId === currentUser?.id);
    };

    const getPlatformLabel = (platform: string) => {
        switch (platform) {
            case 'arena': return 'MTG Arena';
            case 'mtgo': return 'MTGO';
            case 'session': return 'DECKSTR';
            default: return platform;
        }
    };

    const getPlatformColor = (platform: string) => {
        switch (platform) {
            case 'arena': return 'bg-orange-500/15 text-orange-400';
            case 'mtgo': return 'bg-blue-500/15 text-blue-400';
            case 'session': return 'bg-emerald-500/15 text-emerald-400';
            default: return 'bg-white/10 text-white/50';
        }
    };

    // ===== Open chat with specific friend =====
    const openChat = (userId: string) => {
        setActiveChatUserId(userId);
        setActiveView('chat');

        const thread = threads.find(t => t.oderId === userId);
        setChatMessages(thread?.messages || []);

        // Mark messages as read
        if (currentUser && thread && thread.unreadCount > 0) {
            supabase
                .from('whispers')
                .update({ is_read: true })
                .eq('recipient_id', currentUser.id)
                .eq('sender_id', userId)
                .eq('is_read', false)
                .then(() => loadWhisperThreads());
        }

        setTimeout(() => {
            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            messageInputRef.current?.focus();
        }, 100);
    };

    // ===== Send whisper =====
    const sendWhisper = async () => {
        if (!currentUser || !activeChatUserId || !newMessage.trim()) return;
        const messageText = newMessage.trim();
        setIsSending(true);
        setNewMessage('');

        // Optimistically add to chat immediately
        const optimistic: Whisper = {
            id: `opt-${Date.now()}`,
            senderId: currentUser.id,
            recipientId: activeChatUserId,
            content: messageText,
            isRead: false,
            createdAt: new Date().toISOString(),
        };
        setChatMessages(prev => [...prev, optimistic]);
        setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

        try {
            const { error } = await supabase
                .from('whispers')
                .insert({
                    sender_id: currentUser.id,
                    recipient_id: activeChatUserId,
                    content: messageText,
                });

            if (error) {
                console.error('[DECKSTR] Whisper send failed:', error);
                // Remove optimistic message on failure
                setChatMessages(prev => prev.filter(m => m.id !== optimistic.id));
            }

            // Refresh threads in background (picks up bot replies too)
            loadWhisperThreads();
        } catch (err) {
            console.error('[DECKSTR] Whisper send error:', err);
            setChatMessages(prev => prev.filter(m => m.id !== optimistic.id));
        }

        setIsSending(false);
    };

    // ===== Derived data =====
    const onlineFriends = useMemo(() =>
        [DECKSTR_BOT_FRIEND, ...friends.filter(f => f.status === 'online' || f.status === 'away' || f.status === 'busy')],
        [friends]
    );

    const offlineFriends = useMemo(() =>
        friends.filter(f => f.status === 'offline'),
        [friends]
    );

    const activeChatFriend = useMemo(() => {
        if (activeChatUserId === DECKSTR_BOT_USER_ID) return DECKSTR_BOT_FRIEND;
        return friends.find(f => f.oderId === activeChatUserId) || null;
    }, [friends, activeChatUserId]);

    const isChattingWithBot = activeChatUserId === DECKSTR_BOT_USER_ID;

    // Status color
    const statusColor = (status: string) => {
        switch (status) {
            case 'online': return 'bg-emerald-400';
            case 'away': return 'bg-amber-400';
            case 'busy': return 'bg-red-400';
            default: return 'bg-white/20';
        }
    };

    // Time ago
    const timeAgo = (iso: string) => {
        const diff = Date.now() - new Date(iso).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        return `${Math.floor(hrs / 24)}d ago`;
    };

    // Don't render if not logged in
    if (!currentUser) return null;

    // ===== RENDER =====
    return (
        <div className="fixed bottom-14 right-4 z-50 flex flex-col items-end gap-2" style={{ maxHeight: 'calc(100vh - 120px)' }}>
            {/* ===== EXPANDED PANEL ===== */}
            {isOpen && !isMinimized && (
                <div
                    className="w-80 bg-[#0d0d1a]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/50 flex flex-col overflow-hidden"
                    style={{ maxHeight: 'calc(100vh - 180px)' }}
                >
                    {/* Panel header */}
                    <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/10 bg-white/[0.03]">
                        <div className="flex items-center gap-2">
                            <Users className="size-4 text-emerald-400" />
                            <span className="text-sm font-semibold text-white">Friends</span>
                            {onlineFriends.length > 0 && (
                                <span className="text-[10px] text-emerald-400 font-medium">{onlineFriends.length} online</span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => setIsMinimized(true)}
                                className="p-1 text-white/30 hover:text-white/60 transition-colors"
                                title="Minimize"
                            >
                                <Minus className="size-3.5" />
                            </button>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-1 text-white/30 hover:text-white/60 transition-colors"
                                title="Close"
                            >
                                <X className="size-3.5" />
                            </button>
                        </div>
                    </div>

                    {/* Tab nav */}
                    <div className="flex border-b border-white/5">
                        {([
                            { id: 'friends' as BubbleView, icon: Users, label: 'Friends' },
                            { id: 'chat' as BubbleView, icon: MessageCircle, label: 'Whispers', badge: unreadTotal },
                            { id: 'requests' as BubbleView, icon: Bell, label: 'Requests', badge: pendingRequests.length },
                            { id: 'add' as BubbleView, icon: UserPlus, label: 'Add' },
                            { id: 'lfg' as BubbleView, icon: Crosshair, label: 'LFG' },
                        ]).map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => {
                                    setActiveView(tab.id);
                                    if (tab.id === 'chat') setActiveChatUserId(null);
                                }}
                                className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 text-[10px] font-medium transition-colors relative ${activeView === tab.id
                                    ? 'text-emerald-400 border-b-2 border-emerald-400'
                                    : 'text-white/40 hover:text-white/60'
                                    }`}
                            >
                                <tab.icon className="size-3" />
                                {tab.label}
                                {tab.badge ? (
                                    <span className="absolute -top-0.5 right-1 min-w-[14px] h-[14px] flex items-center justify-center bg-rose-500 text-white text-[8px] font-bold rounded-full px-1">
                                        {tab.badge}
                                    </span>
                                ) : null}
                            </button>
                        ))}
                    </div>

                    {/* ===== FRIENDS LIST VIEW ===== */}
                    {activeView === 'friends' && (
                        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', maxHeight: '400px' }}>
                            {isLoadingFriends && friends.length === 0 ? (
                                <div className="flex items-center justify-center py-8 text-white/30">
                                    <Loader2 className="size-4 animate-spin mr-2" />
                                    <span className="text-xs">Loading friends...</span>
                                </div>
                            ) : friends.length === 0 ? (
                                <>
                                    {/* Always show bot in friends list */}
                                    <div>
                                        <div className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-emerald-400/60 font-semibold bg-emerald-500/5">
                                            Online — 1
                                        </div>
                                        <FriendRow
                                            friend={DECKSTR_BOT_FRIEND}
                                            statusColor={statusColor}
                                            onWhisper={() => openChat(DECKSTR_BOT_USER_ID)}
                                            onRemove={() => { }}
                                        />
                                    </div>
                                    <div className="flex flex-col items-center justify-center py-6 text-white/30">
                                        <p className="text-[10px] mb-2">Add friends to play together!</p>
                                        <button
                                            onClick={() => setActiveView('add')}
                                            className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg text-[11px] font-medium border border-emerald-500/30"
                                        >
                                            <UserPlus className="size-3" /> Add Friends
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Online friends */}
                                    {onlineFriends.length > 0 && (
                                        <div>
                                            <div className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-emerald-400/60 font-semibold bg-emerald-500/5">
                                                Online — {onlineFriends.length}
                                            </div>
                                            {onlineFriends.map(friend => (
                                                <FriendRow
                                                    key={friend.id}
                                                    friend={friend}
                                                    statusColor={statusColor}
                                                    onWhisper={() => openChat(friend.oderId)}
                                                    onRemove={() => removeFriend(friend.id)}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {/* Offline friends */}
                                    {offlineFriends.length > 0 && (
                                        <div>
                                            <div className="px-3 py-1.5 text-[9px] uppercase tracking-wider text-white/20 font-semibold">
                                                Offline — {offlineFriends.length}
                                            </div>
                                            {offlineFriends.map(friend => (
                                                <FriendRow
                                                    key={friend.id}
                                                    friend={friend}
                                                    statusColor={statusColor}
                                                    onWhisper={() => openChat(friend.oderId)}
                                                    onRemove={() => removeFriend(friend.id)}
                                                    lastSeen={friend.lastSeenAt ? timeAgo(friend.lastSeenAt) : undefined}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* ===== WHISPERS VIEW ===== */}
                    {activeView === 'chat' && !activeChatUserId && (
                        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', maxHeight: '400px' }}>
                            {/* DECKSTR Bot — always pinned at top */}
                            <button
                                onClick={() => openChat(DECKSTR_BOT_USER_ID)}
                                className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-cyan-500/5 transition-colors text-left border-b border-cyan-500/10"
                            >
                                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-cyan-500/30">
                                    <video src="/jud.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-cyan-300 truncate">
                                            DECKSTR.GG <span className="text-[8px] bg-cyan-500/20 text-cyan-400 px-1 py-0.5 rounded font-semibold">BOT</span>
                                        </span>
                                    </div>
                                    <p className="text-[10px] text-white/30 truncate">
                                        {threads.find(t => t.oderId === DECKSTR_BOT_USER_ID)?.lastMessage || 'Ask me anything about MTG!'}
                                    </p>
                                </div>
                                {(() => {
                                    const botThread = threads.find(t => t.oderId === DECKSTR_BOT_USER_ID);
                                    return botThread && botThread.unreadCount > 0 ? (
                                        <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-cyan-500 text-white text-[9px] font-bold rounded-full px-1 flex-shrink-0">
                                            {botThread.unreadCount}
                                        </span>
                                    ) : null;
                                })()}
                            </button>

                            {/* Regular threads */}
                            {threads.filter(t => t.oderId !== DECKSTR_BOT_USER_ID).length === 0 && (
                                <div className="flex flex-col items-center justify-center py-6 text-white/30">
                                    <MessageCircle className="size-6 mb-2 opacity-20" />
                                    <p className="text-[10px]">No other conversations yet</p>
                                    <p className="text-[9px] text-white/15 mt-1">Click whisper on a friend to start</p>
                                </div>
                            )}
                            {threads.filter(t => t.oderId !== DECKSTR_BOT_USER_ID).map(thread => (
                                <button
                                    key={thread.oderId}
                                    onClick={() => openChat(thread.oderId)}
                                    className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-white/5 transition-colors text-left"
                                >
                                    {/* Avatar */}
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500/30 to-blue-500/30 flex items-center justify-center text-white/60 text-xs font-bold flex-shrink-0">
                                        {thread.displayName[0]?.toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-medium text-white truncate">{thread.displayName}</span>
                                            {thread.lastMessageAt && (
                                                <span className="text-[9px] text-white/20 flex-shrink-0">{timeAgo(thread.lastMessageAt)}</span>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-white/30 truncate">{thread.lastMessage}</p>
                                    </div>
                                    {thread.unreadCount > 0 && (
                                        <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-rose-500 text-white text-[9px] font-bold rounded-full px-1 flex-shrink-0">
                                            {thread.unreadCount}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* ===== ACTIVE CHAT ===== */}
                    {activeView === 'chat' && activeChatUserId && (
                        <div className="flex-1 flex flex-col min-h-0" style={{ maxHeight: '400px' }}>
                            {/* Chat header */}
                            <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5 bg-white/[0.02]">
                                <button
                                    onClick={() => setActiveChatUserId(null)}
                                    className="p-0.5 text-white/30 hover:text-white/60"
                                >
                                    <ChevronDown className="size-3.5 rotate-90" />
                                </button>
                                <div className="relative">
                                    {isChattingWithBot ? (
                                        <div className="w-6 h-6 rounded-full overflow-hidden ring-1 ring-cyan-500/30">
                                            <video src="/jud.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" />
                                        </div>
                                    ) : (
                                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500/30 to-blue-500/30 flex items-center justify-center text-white/60 text-[10px] font-bold">
                                            {activeChatFriend?.displayName[0]?.toUpperCase() || '?'}
                                        </div>
                                    )}
                                    <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#0d0d1a] ${statusColor(activeChatFriend?.status || 'offline')}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="text-xs font-medium text-white truncate block">
                                        {activeChatFriend?.displayName || 'Unknown'}
                                        {isChattingWithBot && <span className="ml-1 text-[8px] bg-cyan-500/20 text-cyan-400 px-1 py-0.5 rounded font-semibold">BOT</span>}
                                    </span>
                                    <span className="text-[9px] text-white/25">{activeChatFriend?.status || 'offline'}</span>
                                </div>
                            </div>

                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto min-h-0 px-3 py-2 space-y-1.5" style={{ scrollbarWidth: 'thin' }}>
                                {chatMessages.length === 0 && (
                                    <div className="text-center py-6 text-white/20 text-[10px]">
                                        {isChattingWithBot ? (
                                            <div className="text-left text-[11px] text-white/50 leading-relaxed whitespace-pre-line">
                                                {BOT_WELCOME_MESSAGE}
                                            </div>
                                        ) : (
                                            'Start of your conversation'
                                        )}
                                    </div>
                                )}
                                {chatMessages.map(msg => {
                                    const isMe = msg.senderId === currentUser.id;
                                    const isBot = msg.senderId === DECKSTR_BOT_USER_ID;
                                    return (
                                        <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                            <div className={`max-w-[75%] px-2.5 py-1.5 rounded-xl text-[11px] leading-relaxed ${isMe
                                                ? 'bg-emerald-500/20 text-emerald-100 rounded-br-sm'
                                                : isBot
                                                    ? 'bg-cyan-500/10 text-cyan-100 rounded-bl-sm border border-cyan-500/20'
                                                    : 'bg-white/[0.07] text-white/80 rounded-bl-sm'
                                                }`}>
                                                {isBot ? renderBotContent(msg.content) : msg.content}
                                                <div className={`text-[8px] mt-0.5 ${isMe ? 'text-emerald-400/30' : 'text-white/15'}`}>
                                                    {timeAgo(msg.createdAt)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                <div ref={chatEndRef} />
                            </div>

                            {/* Message input */}
                            <div className="flex items-center gap-2 px-3 py-2 border-t border-white/5">
                                <input
                                    ref={messageInputRef}
                                    type="text"
                                    value={newMessage}
                                    onChange={e => setNewMessage(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendWhisper()}
                                    placeholder="Type a whisper..."
                                    maxLength={2000}
                                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder:text-white/20 focus:outline-none focus:border-emerald-500/40"
                                />
                                <button
                                    onClick={sendWhisper}
                                    disabled={!newMessage.trim() || isSending}
                                    className="p-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg transition-colors border border-emerald-500/30 disabled:opacity-30"
                                >
                                    {isSending ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ===== FRIEND REQUESTS VIEW ===== */}
                    {activeView === 'requests' && (
                        <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', maxHeight: '400px' }}>
                            {pendingRequests.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-8 text-white/30">
                                    <Bell className="size-8 mb-2 opacity-20" />
                                    <p className="text-xs">No pending requests</p>
                                </div>
                            ) : (
                                pendingRequests.map(req => (
                                    <div key={req.id} className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-white/5 transition-colors">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500/30 to-orange-500/30 flex items-center justify-center text-white/60 text-xs font-bold flex-shrink-0">
                                            {req.displayName[0]?.toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <span className="text-xs font-medium text-white truncate block">{req.displayName}</span>
                                            <span className="text-[9px] text-white/25">wants to be friends</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => handleRequest(req.id, true)}
                                                className="p-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 border border-emerald-500/30"
                                                title="Accept"
                                            >
                                                <Check className="size-3" />
                                            </button>
                                            <button
                                                onClick={() => handleRequest(req.id, false)}
                                                className="p-1.5 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 border border-red-500/20"
                                                title="Decline"
                                            >
                                                <X className="size-3" />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {/* ===== ADD FRIEND VIEW ===== */}
                    {activeView === 'add' && (
                        <div className="flex-1 overflow-y-auto p-3 space-y-3" style={{ scrollbarWidth: 'thin', maxHeight: '400px' }}>
                            {/* Your tag — copyable */}
                            {myTag && (
                                <div className="flex items-center justify-between px-2.5 py-2 bg-white/[0.03] rounded-lg border border-white/5">
                                    <div className="flex items-center gap-1.5">
                                        <Hash className="size-3 text-emerald-400/60" />
                                        <span className="text-[10px] text-white/40">Your tag:</span>
                                        <span className="text-[11px] font-semibold text-white">
                                            {currentUser?.user_metadata?.display_name || currentUser?.email?.split('@')[0]}
                                            <span className="text-emerald-400">#{myTag}</span>
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => {
                                            const name = currentUser?.user_metadata?.display_name || currentUser?.email?.split('@')[0] || '';
                                            navigator.clipboard.writeText(`${name}#${myTag}`);
                                            setTagCopied(true);
                                            setTimeout(() => setTagCopied(false), 2000);
                                        }}
                                        className="p-1 text-white/20 hover:text-emerald-400 transition-colors"
                                        title="Copy your tag"
                                    >
                                        {tagCopied
                                            ? <Check className="size-3 text-emerald-400" />
                                            : <Copy className="size-3" />
                                        }
                                    </button>
                                </div>
                            )}

                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-white/30" />
                                <input
                                    type="text"
                                    value={searchUsername}
                                    onChange={e => {
                                        setSearchUsername(e.target.value);
                                        searchUsers(e.target.value);
                                    }}
                                    placeholder="Name#1234 or username..."
                                    className="w-full pl-8 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder:text-white/25 focus:outline-none focus:border-emerald-500/40"
                                />
                                {isSearching && (
                                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-3.5 text-white/30 animate-spin" />
                                )}
                            </div>

                            {addStatus && (
                                <div className={`text-[10px] text-center py-1 px-2 rounded ${addStatus.includes('sent') ? 'text-emerald-400 bg-emerald-500/10' : 'text-amber-400 bg-amber-500/10'
                                    }`}>
                                    {addStatus}
                                </div>
                            )}

                            {searchResults.length > 0 && (
                                <div className="space-y-1">
                                    {searchResults.map(user => (
                                        <div key={user.user_id} className="flex items-center gap-2.5 px-2 py-2 bg-white/[0.03] rounded-lg">
                                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex items-center justify-center text-white/60 text-[10px] font-bold">
                                                {user.display_name[0]?.toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <span className="text-xs text-white/80 truncate block">
                                                    {user.display_name}
                                                    {user.friend_tag && (
                                                        <span className="text-emerald-400/60 text-[10px]">#{user.friend_tag}</span>
                                                    )}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => sendFriendRequest(user.user_id)}
                                                className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-[10px] font-medium hover:bg-emerald-500/30 border border-emerald-500/30"
                                            >
                                                <Plus className="size-3" /> Add
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {searchUsername.length >= 2 && searchResults.length === 0 && !isSearching && (
                                <div className="text-center text-[10px] text-white/20 py-4">
                                    No users found matching "{searchUsername}"
                                </div>
                            )}

                            {searchUsername.length < 2 && (
                                <div className="text-center text-[10px] text-white/20 py-4">
                                    Enter a name or Name#1234 to search
                                </div>
                            )}
                        </div>
                    )}

                    {/* ===== LFG VIEW ===== */}
                    {activeView === 'lfg' && (
                        <div className="flex-1 overflow-y-auto p-3 space-y-3" style={{ scrollbarWidth: 'thin', maxHeight: '400px' }}>
                            {/* Host a Game */}
                            <div className="space-y-2 p-2.5 bg-white/[0.03] rounded-lg border border-white/5">
                                <div className="text-[9px] uppercase tracking-wider text-emerald-400/60 font-semibold">
                                    Host a Game
                                </div>
                                <div className="flex gap-2">
                                    <select
                                        value={lfgFormat}
                                        onChange={e => setLfgFormat(e.target.value)}
                                        className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-white focus:outline-none focus:border-emerald-500/40"
                                    >
                                        {['Standard', 'Modern', 'Pioneer', 'Commander', 'Draft', 'Sealed', 'Pauper', 'Legacy', 'Vintage', 'Casual'].map(f => (
                                            <option key={f} value={f} className="bg-[#0d0d1a]">{f}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={lfgPlatform}
                                        onChange={e => setLfgPlatform(e.target.value as any)}
                                        className="w-20 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-white focus:outline-none focus:border-emerald-500/40"
                                    >
                                        <option value="session" className="bg-[#0d0d1a]">DECKSTR</option>
                                        <option value="arena" className="bg-[#0d0d1a]">Arena</option>
                                        <option value="mtgo" className="bg-[#0d0d1a]">MTGO</option>
                                    </select>
                                    <select
                                        value={lfgMaxPlayers}
                                        onChange={e => setLfgMaxPlayers(Number(e.target.value))}
                                        className="w-12 bg-white/5 border border-white/10 rounded-lg px-1 py-1.5 text-[10px] text-white focus:outline-none focus:border-emerald-500/40"
                                    >
                                        {[2, 3, 4, 5, 6, 7, 8].map(n => (
                                            <option key={n} value={n} className="bg-[#0d0d1a]">{n}p</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={lfgMessage}
                                        onChange={e => setLfgMessage(e.target.value)}
                                        placeholder="Casual Commander, no stax..."
                                        maxLength={120}
                                        className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-[10px] text-white placeholder:text-white/20 focus:outline-none focus:border-emerald-500/40"
                                    />
                                    <button
                                        onClick={handleCreateGame}
                                        disabled={isPostingLfg}
                                        className="px-2.5 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg text-[10px] font-medium hover:bg-emerald-500/30 border border-emerald-500/30 disabled:opacity-30"
                                    >
                                        {isPostingLfg ? <Loader2 className="size-3 animate-spin" /> : 'Host'}
                                    </button>
                                </div>
                            </div>

                            {/* Active Games */}
                            <div className="text-[9px] uppercase tracking-wider text-white/30 font-semibold flex items-center justify-between">
                                <span>Active Games</span>
                                <button
                                    onClick={loadLfgGames}
                                    className="text-[8px] text-emerald-400/50 hover:text-emerald-400 transition-colors"
                                >
                                    Refresh
                                </button>
                            </div>

                            {isLoadingLfg ? (
                                <div className="flex items-center justify-center py-4">
                                    <Loader2 className="size-4 animate-spin text-white/30" />
                                </div>
                            ) : lfgGames.length === 0 ? (
                                <div className="text-center py-6 text-white/20">
                                    <Crosshair className="size-6 mx-auto mb-2 opacity-20" />
                                    <p className="text-[10px]">No active games</p>
                                    <p className="text-[9px] text-white/10 mt-1">Host a game to get started!</p>
                                </div>
                            ) : (
                                <div className="space-y-1.5">
                                    {lfgGames.map(game => {
                                        const isHost = game.hostId === currentUser?.id;
                                        const myStatus = getPlayerStatus(game.id);
                                        const gamePlayers = lfgPlayers[game.id] || [];
                                        const pendingPlayers = gamePlayers.filter(p => p.status === 'pending');
                                        const acceptedPlayers = gamePlayers.filter(p => p.status === 'accepted');
                                        const isExpanded = expandedGame === game.id;

                                        return (
                                            <div key={game.id} className="bg-white/[0.03] rounded-lg border border-white/5 hover:border-white/10 transition-colors">
                                                {/* Game Header */}
                                                <button
                                                    className="w-full px-2.5 py-2 text-left"
                                                    onClick={() => setExpandedGame(isExpanded ? null : game.id)}
                                                >
                                                    <div className="flex items-center justify-between mb-1">
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-[10px] font-medium text-white/80">{game.hostDisplayName}</span>
                                                            {isHost && <span className="text-[7px] bg-amber-500/20 text-amber-400 px-1 py-0.5 rounded font-bold">HOST</span>}
                                                        </div>
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-[8px] text-white/25">
                                                                {game.currentPlayers}/{game.maxPlayers}
                                                            </span>
                                                            {game.queueOpen ? (
                                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" title="Queue open" />
                                                            ) : (
                                                                <span className="w-1.5 h-1.5 rounded-full bg-red-400" title="Queue closed" />
                                                            )}
                                                            <Clock className="size-2.5 text-white/15" />
                                                            <span className="text-[8px] text-white/15">{timeAgo(game.createdAt)}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 mb-1">
                                                        <span className="text-[8px] bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded font-semibold">{game.format}</span>
                                                        <span className={`text-[8px] px-1.5 py-0.5 rounded font-semibold ${getPlatformColor(game.platform)}`}>{getPlatformLabel(game.platform)}</span>
                                                        {pendingPlayers.length > 0 && isHost && (
                                                            <span className="text-[7px] bg-amber-500/20 text-amber-300 px-1 py-0.5 rounded animate-pulse">
                                                                {pendingPlayers.length} pending
                                                            </span>
                                                        )}
                                                    </div>
                                                    {game.message && <p className="text-[10px] text-white/40 truncate">{game.message}</p>}
                                                </button>

                                                {/* Expanded Game Details */}
                                                {isExpanded && (
                                                    <div className="px-2.5 pb-2.5 space-y-2 border-t border-white/5 pt-2">
                                                        {/* Accepted Players */}
                                                        {acceptedPlayers.length > 0 && (
                                                            <div>
                                                                <div className="text-[8px] text-white/25 uppercase tracking-wider mb-1">Players</div>
                                                                {acceptedPlayers.map(p => (
                                                                    <div key={p.id} className="flex items-center justify-between py-0.5">
                                                                        <div className="flex items-center gap-1.5">
                                                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                                                                            <span className="text-[10px] text-white/70">{p.displayName}</span>
                                                                        </div>
                                                                        {isHost && (
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); handleRespondToPlayer(p.id, 'declined'); }}
                                                                                className="text-[8px] text-red-400/40 hover:text-red-400 transition-colors"
                                                                            >
                                                                                Kick
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* Pending Requests (host only) */}
                                                        {isHost && pendingPlayers.length > 0 && (
                                                            <div>
                                                                <div className="text-[8px] text-amber-400/50 uppercase tracking-wider mb-1">Join Requests</div>
                                                                {pendingPlayers.map(p => (
                                                                    <div key={p.id} className="flex items-center justify-between py-1 px-1.5 bg-amber-500/5 rounded">
                                                                        <span className="text-[10px] text-white/60">{p.displayName}</span>
                                                                        <div className="flex gap-1">
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); handleRespondToPlayer(p.id, 'accepted'); }}
                                                                                className="px-1.5 py-0.5 text-[8px] bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30 transition-colors"
                                                                            >
                                                                                Accept
                                                                            </button>
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); handleRespondToPlayer(p.id, 'declined'); }}
                                                                                className="px-1.5 py-0.5 text-[8px] bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
                                                                            >
                                                                                Decline
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* Platform username (shown to accepted players) */}
                                                        {myStatus?.status === 'accepted' && (
                                                            <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                                                                <div className="text-[8px] text-emerald-400/60 uppercase tracking-wider mb-1">Connect on {getPlatformLabel(game.platform)}</div>
                                                                <p className="text-[10px] text-emerald-300 font-medium">
                                                                    Host: {game.hostDisplayName}
                                                                </p>
                                                                <p className="text-[9px] text-white/40 mt-0.5">
                                                                    {game.platform === 'session'
                                                                        ? 'Join via the Sessions tab'
                                                                        : `Add them on ${getPlatformLabel(game.platform)} to start playing`
                                                                    }
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* Actions */}
                                                        <div className="flex gap-1.5">
                                                            {isHost ? (
                                                                <>
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); handleToggleQueue(game.id, !game.queueOpen); }}
                                                                        className={`flex-1 px-2 py-1 rounded text-[9px] font-medium border transition-colors ${game.queueOpen
                                                                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20'
                                                                            : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20'
                                                                            }`}
                                                                    >
                                                                        {game.queueOpen ? 'Close Queue' : 'Open Queue'}
                                                                    </button>
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); handleCloseGame(game.id); }}
                                                                        className="px-2 py-1 bg-red-500/10 text-red-400 rounded text-[9px] font-medium hover:bg-red-500/20 border border-red-500/20 transition-colors"
                                                                    >
                                                                        End
                                                                    </button>
                                                                </>
                                                            ) : !myStatus ? (
                                                                game.queueOpen && game.status === 'open' ? (
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); handleJoinGame(game.id); }}
                                                                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-emerald-500/15 text-emerald-400 rounded text-[9px] font-medium hover:bg-emerald-500/25 border border-emerald-500/25 transition-colors"
                                                                    >
                                                                        Join Game
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-[9px] text-white/20 italic">Queue closed</span>
                                                                )
                                                            ) : myStatus.status === 'pending' ? (
                                                                <div className="flex-1 flex items-center justify-between">
                                                                    <span className="text-[9px] text-amber-400/70 italic">Waiting for host...</span>
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); handleLeaveGame(game.id); }}
                                                                        className="text-[8px] text-red-400/40 hover:text-red-400 transition-colors"
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            ) : myStatus.status === 'accepted' ? (
                                                                <div className="flex-1 flex items-center justify-between">
                                                                    <span className="text-[9px] text-emerald-400 font-medium">✓ You're in!</span>
                                                                    <button
                                                                        onClick={(e) => { e.stopPropagation(); handleLeaveGame(game.id); }}
                                                                        className="text-[8px] text-red-400/40 hover:text-red-400 transition-colors"
                                                                    >
                                                                        Leave
                                                                    </button>
                                                                </div>
                                                            ) : myStatus.status === 'declined' ? (
                                                                <span className="text-[9px] text-red-400/50 italic">Request declined</span>
                                                            ) : null}

                                                            {/* Whisper host */}
                                                            {!isHost && (
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); openChat(game.hostId); }}
                                                                    className="px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded text-[9px] font-medium hover:bg-cyan-500/20 border border-cyan-500/20 transition-colors"
                                                                    title="Message host"
                                                                >
                                                                    <MessageCircle className="size-3" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ===== BUBBLE BUTTON ===== */}
            <button
                onClick={() => {
                    if (isMinimized) {
                        setIsMinimized(false);
                        setIsOpen(true);
                    } else {
                        setIsOpen(prev => !prev);
                    }
                }}
                className={`relative w-12 h-12 rounded-full shadow-lg shadow-black/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95 ${isOpen && !isMinimized
                    ? 'bg-emerald-500/30 border-2 border-emerald-500/40'
                    : 'bg-[#1a1a2e]/90 backdrop-blur-sm border-2 border-white/10 hover:border-emerald-500/30'
                    }`}
            >
                <Users className={`size-5 ${isOpen && !isMinimized ? 'text-emerald-400' : 'text-white/60'}`} />

                {/* Online count badge */}
                {onlineFriends.length > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-emerald-500 text-white text-[9px] font-bold rounded-full px-1 shadow-lg">
                        {onlineFriends.length}
                    </span>
                )}

                {/* Unread whisper badge */}
                {unreadTotal > 0 && (
                    <span className="absolute -top-1 -left-1 min-w-[18px] h-[18px] flex items-center justify-center bg-rose-500 text-white text-[9px] font-bold rounded-full px-1 shadow-lg">
                        {unreadTotal}
                    </span>
                )}
            </button>
        </div>
    );
}

// ===== Friend Row Sub-component =====
function FriendRow({
    friend,
    statusColor,
    onWhisper,
    onRemove,
    lastSeen,
}: {
    friend: Friend;
    statusColor: (s: string) => string;
    onWhisper: () => void;
    onRemove: () => void;
    lastSeen?: string;
}) {
    const [showActions, setShowActions] = useState(false);

    const isBot = friend.oderId === DECKSTR_BOT_USER_ID;

    return (
        <div
            className={`flex items-center gap-2.5 px-3 py-2 hover:bg-white/5 transition-colors group ${isBot ? 'border-b border-cyan-500/10' : ''}`}
            onMouseEnter={() => setShowActions(true)}
            onMouseLeave={() => setShowActions(false)}
        >
            {/* Avatar with status indicator */}
            <div className="relative flex-shrink-0">
                {isBot ? (
                    <div className="w-8 h-8 rounded-full overflow-hidden ring-1 ring-cyan-500/30">
                        <video src="/jud.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" />
                    </div>
                ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500/30 to-blue-500/30 flex items-center justify-center text-white/60 text-xs font-bold">
                        {friend.displayName[0]?.toUpperCase()}
                    </div>
                )}
                <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#0d0d1a] ${statusColor(friend.status)}`} />
            </div>

            {/* Name + activity */}
            <div className="flex-1 min-w-0">
                <span className={`text-xs font-medium truncate block ${isBot ? 'text-cyan-300' : 'text-white'}`}>
                    {friend.displayName}
                    {isBot && <span className="ml-1 text-[8px] bg-cyan-500/20 text-cyan-400 px-1 py-0.5 rounded font-semibold">BOT</span>}
                </span>
                {friend.currentActivity ? (
                    <span className="text-[9px] text-white/25 truncate block">{friend.currentActivity}</span>
                ) : lastSeen ? (
                    <span className="text-[9px] text-white/15 truncate block">Last seen {lastSeen}</span>
                ) : null}
            </div>

            {/* Action buttons (visible on hover) */}
            {showActions && (
                <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                        onClick={onWhisper}
                        className="p-1 text-white/30 hover:text-emerald-400 transition-colors"
                        title="Whisper"
                    >
                        <MessageCircle className="size-3.5" />
                    </button>
                    {!isBot && (
                        <button
                            onClick={onRemove}
                            className="p-1 text-white/30 hover:text-red-400 transition-colors"
                            title="Remove friend"
                        >
                            <UserMinus className="size-3.5" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
